import React, { useState } from 'react'
import { faPen, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import palette from 'src/designSystem/variables/palette'
import { FlexRow, FlexAlign } from 'src/components/Reusable/Flex'
import {
  IconButton,
  IconButtonVariant,
} from 'src/components/Reusable/Buttons/IconButton'
import { EditableWrapper } from './EditableField.styles'

interface ConfirmCancelButtonsProps {
  onConfirm: (e: React.MouseEvent) => void
  onCancel: (e: React.MouseEvent) => void
  disabled?: boolean
  justify?: FlexAlign
  style?: React.CSSProperties
}

export const ConfirmCancelButtons = ({
  onConfirm,
  onCancel,
  disabled = false,
  justify = FlexAlign.FLEX_END,
  style,
}: ConfirmCancelButtonsProps): JSX.Element => (
  <FlexRow gap={8} justify={justify} style={style}>
    <IconButton
      onClick={onConfirm}
      disabled={disabled}
      type="button"
      variant={IconButtonVariant.icon}
    >
      <FontAwesomeIcon icon={faCheck} />
    </IconButton>
    <IconButton
      onClick={(e) => {
        e.stopPropagation()
        onCancel(e)
      }}
      type="button"
      variant={IconButtonVariant.icon}
    >
      <FontAwesomeIcon icon={faTimes} />
    </IconButton>
  </FlexRow>
)

interface EditableFieldProps {
  renderView: () => React.ReactNode
  children: React.ReactNode
  onEdit?: () => void
  onCancel?: () => void
  onSave?: (e: React.FormEvent | React.MouseEvent) => void
  isEditing?: boolean
  isSaving?: boolean
  disabled?: boolean
  fullWidth?: boolean
}

export const EditableField = ({
  children,
  renderView,
  isEditing: isEditingProp,
  onEdit,
  onCancel,
  onSave,
  isSaving = false,
  disabled = false,
  fullWidth = false,
}: EditableFieldProps): JSX.Element => {
  const [internalIsEditing, setInternalIsEditing] = useState(false)
  const isEditing =
    isEditingProp !== undefined ? isEditingProp : internalIsEditing

  const [isHovered, setIsHovered] = useState(false)

  const handleStartEdit = () => {
    if (isEditingProp === undefined) {
      setInternalIsEditing(true)
    }
    onEdit?.()
  }

  const handleCancel = () => {
    if (isEditingProp === undefined) {
      setInternalIsEditing(false)
    }
    onCancel?.()
  }

  const handleSave = (e: React.FormEvent | React.MouseEvent) => {
    onSave?.(e)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
      handleSave(e)
    }
    if (e.key === 'Escape') {
      e.stopPropagation()
      handleCancel()
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  if (isEditing) {
    return (
      <div
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        style={fullWidth ? { width: '100%' } : undefined}
      >
        <FlexRow
          isFullWidth={fullWidth}
          gap={8}
          align={FlexAlign.CENTER}
          justify={FlexAlign.FLEX_START}
        >
          <div style={{ flex: 1 }}>{children}</div>
          <ConfirmCancelButtons
            onConfirm={handleSave}
            onCancel={handleCancel}
            disabled={isSaving}
            justify={FlexAlign.FLEX_START}
          />
        </FlexRow>
      </div>
    )
  }

  if (disabled) {
    return <>{renderView()}</>
  }

  return (
    <EditableWrapper
      onClick={handleStartEdit}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {renderView()}
      {isHovered && (
        <FontAwesomeIcon
          icon={faPen}
          style={{
            fontSize: '10px',
            marginLeft: '4px',
            color: palette.neutral[400],
          }}
        />
      )}
    </EditableWrapper>
  )
}
